import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageCircle, Send, X } from "lucide-react";

// Firebase RTDB REST endpoint — bypasses the Firebase Web SDK entirely.
// We had repeated regressions with the SDK inside Capacitor iOS WKWebView
// (WebSocket / long-polling silently failing, no error surfaced). The REST
// API is plain HTTPS, which always works in any WebView. Latency is
// ~2 s with the polling interval below — acceptable for a ride chat.
const RTDB_BASE = (process.env.REACT_APP_FIREBASE_DATABASE_URL || "").replace(/\/$/, "");
const POLL_INTERVAL_MS = 2000;

const QUICK_REPLIES = {
  driver: [
    "J'arrive dans 5 min",
    "Je suis devant l'hôtel",
    "Léger retard, 10 min",
    "J'ai un bouchon, patience svp",
    "Je vous attends",
  ],
  client: [
    "Je descends",
    "J'arrive dans 2 min",
    "Bagages prêts",
    "Merci !",
    "Je suis en retard, attendez svp",
  ],
  hotel: [
    "Le client descend",
    "Le client est dans le lobby",
    "Client prêt, bagages avec lui",
    "Merci !",
  ],
};

/**
 * Realtime chat between client ↔ driver ↔ hotel over Firebase RTDB REST.
 *
 * Storage: `chats/{token}/messages/{pushId}` = { author, label, text, at }
 * The tracking_token acts as a shared secret for the 3 parties of a ride.
 */
export default function ChatBox({ token, author, label, className = "" }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  // ── Polling-based read (REST, no Firebase SDK) ────────────────────────
  useEffect(() => {
    if (!token || !RTDB_BASE) return;
    let cancelled = false;
    const url = `${RTDB_BASE}/chats/${token}/messages.json`;
    const fetchMessages = async () => {
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) return;
        const v = (await r.json()) || {};
        if (cancelled) return;
        const arr = Object.entries(v)
          .map(([id, m]) => ({ id, ...m }))
          .sort((a, b) => (a.at || 0) - (b.at || 0));
        setMessages(arr);
      } catch (_) { /* silent — next tick will retry */ }
    };
    fetchMessages();
    const iv = setInterval(fetchMessages, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [token]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (e, overrideText) => {
    e?.preventDefault?.();
    const t = (overrideText ?? text).trim();
    if (!t || !token || !RTDB_BASE) return;
    setSending(true);
    try {
      // RTDB REST: POST to /chats/{token}/messages.json with a JSON body
      // creates a new pushId and returns { name: pushId } on success.
      const res = await fetch(`${RTDB_BASE}/chats/${token}/messages.json`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          author, label, text: t,
          at: Date.now(), // millisecond timestamp (RTDB serverTimestamp is SDK-only)
        }),
      });
      if (!res.ok) throw new Error(`Firebase ${res.status}`);
      if (!overrideText) setText("");
      // Immediate optimistic refresh — don't wait for the next poll tick.
      try {
        const r = await fetch(`${RTDB_BASE}/chats/${token}/messages.json`, { cache: "no-store" });
        if (r.ok) {
          const v = (await r.json()) || {};
          const arr = Object.entries(v)
            .map(([id, m]) => ({ id, ...m }))
            .sort((a, b) => (a.at || 0) - (b.at || 0));
          setMessages(arr);
        }
      } catch (_) {}
      // Best-effort: notify the other parties via OneSignal backend push.
      try {
        await api.post("/chat/notify", { token, author, label, text: t });
      } catch (_) {}
    } catch (err) {
      console.error("chat send failed", err);
      const { toast } = await import("sonner");
      toast.error("Message non envoyé — vérifiez votre connexion.");
    } finally { setSending(false); }
  };

  const replies = QUICK_REPLIES[author] || [];

  // Quick reply handler — extracted so we can wire BOTH onClick AND
  // onPointerUp. iOS WKWebView occasionally drops onClick on chips inside
  // an overflow-scrolling container (the tap gets interpreted as a scroll
  // gesture by the gesture recognizer). onPointerUp wins the race when
  // available, onClick is the universal fallback.
  const sendingRef = useRef(false);
  const handleQuickReply = (reply) => {
    if (sendingRef.current) return; // de-dup successive pointer/click events
    sendingRef.current = true;
    send(null, reply).finally(() => { sendingRef.current = false; });
  };

  return (
    <div className={`flex h-full min-h-[420px] w-full max-w-full flex-col overflow-hidden rounded-2xl border border-border bg-card ${className}`} data-testid="chat-box">
      <div className="flex flex-none items-center gap-2 border-b border-border bg-secondary/40 px-4 py-3">
        <div className="grid h-7 w-7 place-items-center rounded-full bg-primary/10 text-primary">
          <MessageCircle className="h-3.5 w-3.5" />
        </div>
        <p className="font-[Outfit] text-sm font-medium">Messagerie de la course</p>
      </div>

      <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto overflow-x-hidden px-4 py-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <p className="mt-8 text-center text-xs text-muted-foreground">
            Aucun message. Envoyez un message pour démarrer la conversation.
          </p>
        )}
        {messages.map((m) => {
          const mine = m.author === author;
          return (
            <div key={m.id} className={`flex w-full ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] break-words rounded-2xl px-3 py-2 text-sm shadow-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                {!mine && m.label && (
                  <p className="mb-0.5 text-[10px] uppercase tracking-widest opacity-70">{m.label}</p>
                )}
                <p className="whitespace-pre-wrap break-words leading-snug">{m.text}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick replies — INTENTIONALLY rendered OUTSIDE the <form>. Inside
          a <form>, even with `type="button"`, iOS WKWebView occasionally
          fires a synthetic submit when the user taps a chip while the
          form's input is focused. Moving them out kills that whole class
          of regressions. */}
      {replies.length > 0 && (
        <div
          className="flex flex-none flex-wrap gap-1.5 overflow-x-hidden border-t border-border bg-background px-3 pt-2.5 pb-1"
          data-testid="quick-replies"
        >
          {replies.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => handleQuickReply(r)}
              onPointerUp={(e) => { e.preventDefault(); handleQuickReply(r); }}
              disabled={sending}
              style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              className="max-w-full whitespace-nowrap rounded-full border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-foreground active:scale-95 active:bg-primary/10 disabled:opacity-50"
              data-testid={`quick-reply-${r.slice(0,10)}`}
            >
              {r}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={send} className="flex-none bg-background">
        <div className="flex items-center gap-2 px-3 py-2.5">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Écrire un message…"
            className="w-full min-w-0 flex-1 rounded-full border border-border bg-card px-4 py-2 text-sm outline-none focus:border-primary"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={!text.trim() || sending}
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
            className="grid h-9 w-9 flex-none place-items-center rounded-full bg-primary text-primary-foreground transition-transform active:scale-95 disabled:opacity-50"
            data-testid="chat-send"
            aria-label="Envoyer"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>
    </div>
  );
}

/** Collapsible chat toggle — appears as a pill that expands into <ChatBox /> */
export function ChatToggle({ token, author, label, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!token) return null;
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        className="gap-1.5"
        data-testid="chat-toggle"
      >
        {open ? <X className="h-3.5 w-3.5" /> : <MessageCircle className="h-3.5 w-3.5" />}
        {open ? "Fermer le chat" : "Ouvrir le chat"}
      </Button>
      {open && (
        <div className="mt-3">
          <ChatBox token={token} author={author} label={label} />
        </div>
      )}
    </>
  );
}
